import { ChangeDetectionStrategy, Component, DestroyRef, inject } from '@angular/core';
import { API, ColorDefinitionDTO } from '../../../services/api/api';
import { UtilService } from '../../../services/util.service';
import { SbSplitButton } from '../../../services/global/global-interfaces';
import { ErrorService } from '../../../services/error.service';
import { ModalService } from '../../../services/gui/modal/modal.service';
import { TranslateService } from '@ngx-translate/core';
import { PermissionService } from '../../../services/permission.service';
import { finalize, take } from 'rxjs/operators';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { FilterPanelConfig } from '@ngx-seatback/layout';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';
import { TagService } from '../../../services/tag.service';
import { RoutingID } from '../../../routes/sb-routing-enum';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RequestClass } from '../../../services/global/RequestClass';

type SplitButtonAction = 'edit' | 'delete';

@Component({
  selector: 'sb-page-colors',
  templateUrl: './colors.component.html',
  styleUrls: ['./colors.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ColorsComponent extends RequestClass {
  public readonly tagService = inject(TagService);
  public readonly util = inject(UtilService);
  public readonly permissionService = inject(PermissionService);
  private readonly api = inject(API);
  private readonly translate = inject(TranslateService);
  private readonly modalService = inject(ModalService);
  private readonly errorService = inject(ErrorService);
  private readonly stringTransformation = inject(StringTransformationService);
  private readonly destroyRef = inject(DestroyRef);

  // table wrapper variables
  colors: ColorDefinitionDTO[] = [];

  // filter variables
  exactMatch: SearchOptions = 'contains';
  search: string | null = null;
  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    hideTagSection: true,
    button: { visibleWithoutChanges: true },
  };

  refreshFromServer() {
    if (!this.pagination) {
      this.loading.set(false);
      return;
    }
    const sub = this.api
      .shared_ColorDefinition_getColorDefinitions(
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
        },
        this.pagination
      )
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (colors: ColorDefinitionDTO[]) => {
          this.colors = this.buildSplitButtons(colors);
        },
        error: error => {
          this.errorService.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  deleteColor(color: ColorDefinitionDTO) {
    this.modalService
      .confirm({
        title: this.translate.instant('lang.globals.deleteColor'),
        message: this.translate.instant('lang.globals.deleteColorMessage'),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (result) {
          this.api
            .shared_ColorDefinition_deleteColorDefinition(color.id)
            .pipe(take(1))
            .subscribe({
              next: () => {
                this.refreshFromServer();
              },
              error: error => {
                this.errorService.errorHandler(error);
              },
            });
        }
      })
      .catch(() => {});
  }

  buildSplitButtons<T extends { id: any }>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      if (this.permissionService.hasPermissions([this.permissionService.Permission.color_definition_edit])) {
        item.sbButtons.push({
          icon: 'edit',
          tooltip: 'lang.globals.edit',
          link: RoutingID.colorEdit,
          pathParams: [{ id: 'colorID', value: item?.id }],
        });
      }
      if (this.permissionService.hasPermissions([this.permissionService.Permission.color_definition_delete])) {
        item.sbButtons.push({
          icon: 'trash',
          iconClass: 'hover:!tw-bg-sbRed-500 *:tw-text-sbRed-500',
          action: 'delete',
          tooltip: 'lang.globals.delete',
        });
      }
    });
    return result;
  }

  resolveSplitButtonAction(color: ColorDefinitionDTO, $event: SplitButtonAction) {
    switch ($event) {
      case 'delete':
        this.deleteColor(color);
        break;
      default:
        console.error('Missing action implementation of Splitbutton');
        break;
    }
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
  }
}
